import { useState, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { cartQueryKey, registrationQueryKey, registrationUpsell, type Order, type OrderItem, type Registration } from '@/api/registration';
import { type ApiResponse, type ApiResponseSingle } from '@/api/tenantClient';
import { generatePageUrl } from '@/app/pages';
import BaseMessageBarError from '@/base/MessageBar/Error';
import QueryContainer from '@/base/QueryContainer/QueryContainer';
import {
  UiButton,
  UiHStack, UiIconPencilSimple,
  UiIconPlus, UiIconPlusCircle,
  UiStack,
  uiStyles,
  UiTable,
  UiTbody,
  UiTd,
  UiText,
  UiTh,
  UiThead,
  UiTr,
} from '@/lib/ui';
import { useCartQuery } from '@/registration/hook/useCartQuery';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useUserSession } from '@/registration/hook/useUserSession';
import BaseButtonGhost from '@/base/Button/Ghost';
import BaseButtonIconText from '@/base/Button/IconText';

interface SessionAddonProps {
  orderListQuery: UseQueryResult<ApiResponse<Order>, Error>,
  registration: Registration
}

const SessionAddonList: FC<SessionAddonProps> = ({ orderListQuery, registration }) => {
  const { createTenantApiRequest, isLoading: isApiPreparing } = useTenantApi();
  const { tenantCode, eventId } = useRegisterRoute();
  const { userSession } = useUserSession();
  const [errorMessage, setErrorMessage] = useState<string[] | undefined>([]);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: cart } = useCartQuery(eventId);
  const { mutate: registrationUpsellMutate } = useMutation<ApiResponseSingle<Registration>, Error, { eventId: string }>({
    mutationFn: async (params: { eventId: string }) => {
      return await registrationUpsell(createTenantApiRequest)(params);
    },
    onSuccess: async (data: ApiResponseSingle<Registration>) => {
      if (data.errors && data.errors.length > 0) {
        setErrorMessage(data.errors);
      } else {
        await queryClient.invalidateQueries({ queryKey: [registrationQueryKey, { eventId }, { token: userSession?.authToken }] });
        void queryClient.invalidateQueries({ queryKey: [cartQueryKey, { registrationId: cart?.registrationId }] });
        navigate(generatePageUrl(data.item.registrationUpsellSteps[0]?.routeName, { tenantCode, eventId }));
      }
    },
    onError: (registrationError: any) => {
      throw registrationError;
    }
  });

  const onBuyAdditionalTickets = () => {
    registrationUpsellMutate({ eventId });
  };

  return (
    <QueryContainer query={orderListQuery} errorMessage="Failed to load session addon data.">
      {(ordersData) => {
        const errors = ordersData?.errors ?? [];
        const orders = ordersData?.items ?? [];

        return (
          <UiStack spacing={4}>
            <UiHStack spacing={4}>
              <UiText fontWeight={'bold'}>
                Additional tickets
              </UiText>
              {/*<UiButton onClick={onBuyAdditionalTickets}>*/}
              {/*  Buy additional tickets*/}
              {/*</UiButton>*/}
              {!registration.groupRegistrationId && (
                // <BaseButtonGhost
                //   LeftIcon={UiIconPlusCircle}
                //   onClick={onBuyAdditionalTickets}
                // >
                //   Buy more
                // </BaseButtonGhost>
                <BaseButtonIconText Icon={UiIconPlusCircle} onClick={onBuyAdditionalTickets}>Buy more</BaseButtonIconText>
              )}
            </UiHStack>
            {((errors && errors.length > 0) || (errorMessage?.length && errorMessage?.length > 0)) && (
              <UiStack flexGrow={1}>
                {errors.map((error) => {
                  return (
                    <BaseMessageBarError key={error}>{error}</BaseMessageBarError>
                  );
                })}
                {errorMessage?.map((error) => {
                  return (
                    <BaseMessageBarError key={error}>{error}</BaseMessageBarError>
                  );
                })}
              </UiStack>
            )}
            <UiStack
              p={8}
              bgColor={'#fff'}
              borderRadius={uiStyles.borderRadius}
              borderWidth={'1px'}
              borderColor={'gray.300'}
              borderStyle={'solid'}
            >
              <UiTable>
                <UiThead>
                  <UiTr>
                    <UiTh>
                      <UiText variant='body2' alignContent={'center'}>
                        Ticket name
                      </UiText>
                    </UiTh>
                    <UiTh>
                      <UiText variant='body2' alignContent={'center'}>
                        Quantity
                      </UiText>
                    </UiTh>
                  </UiTr>
                </UiThead>
                <UiTbody>
                  {orders
                    .filter((order: Order) => order.status !== 'Cancelled')
                    .map((order: Order, orderIndex) => (
                      order.orderItems
                        ?.filter((item: OrderItem) => !item.mainTicket)
                        .map((item, itemIndex) => {
                        // console.log(orders, order, item);
                        /**
                         * Both order id and item id are NOT returned by the backend. WHY????
                         * Had to use the meaningless index as the key here.
                         */
                        return (
                          <UiTr key={`${orderIndex}-${itemIndex}`}>
                            <UiTd>
                              <UiText variant='body2' alignContent={'center'}>
                                {item.name}
                              </UiText>
                            </UiTd>
                            <UiTd>
                              <UiText variant='body2' alignContent={'center'}>
                                {item.quantity}
                              </UiText>
                            </UiTd>
                          </UiTr>
                        );
                      })
                    )).flat()}
                </UiTbody>
              </UiTable>
            </UiStack>
          </UiStack>
        );
      }}
    </QueryContainer>
  );
};

export default SessionAddonList;
